import { useTranslation } from "react-i18next";

import type { DateValueType } from "../../DatepickerV2";

import { Button as ButtonV2 } from "../../Button";
import Datepicker from "../../DatepickerV2";
import { FieldTrigger } from "../../DatepickerV2/components/FieldTrigger";
import { getShortcutButtonLabel } from "../../DatepickerV2/helpers/shortcutLabel";
import { useDatepickerTranslations } from "../../hooks/useDatepickerTranslations";
import { CrossIcon, FilterIcon, SearchIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { useSubIBReportContext } from "../providers/SubIBReportProvider";

type SubIBsToolbarProps = {
  filterCount: number;
  onFilterOpen: () => void;
  onExport: () => void;
};

const SEARCH_INPUT_CLASSNAME =
  "h-10 w-full rounded-lg border-none bg-surface-tonal-default pl-10 pr-9 typo-body-sm text-text-primary outline-none placeholder:text-text-subdued hover:bg-surface-tonal-bold focus:outline-none focus:ring-2 focus:ring-stroke-focused";

const toDateValue = (range: {
  from: Date | null;
  to: Date | null;
}): DateValueType =>
  range.from || range.to ? { startDate: range.from, endDate: range.to } : null;

const toDateRange = (
  value: DateValueType,
): { from: Date | null; to: Date | null } => ({
  from: value?.startDate ?? null,
  to: value?.endDate ?? null,
});

export const SubIBsToolbar = ({
  filterCount,
  onFilterOpen,
  onExport,
}: SubIBsToolbarProps) => {
  const { t } = useTranslation();
  const { state, actions } = useSubIBReportContext();
  // Shared with `getShortcutButtonLabel` — the label can only collapse a range
  // to a preset name if it compares against the same configs the picker uses.
  const datepickerConfigs = useDatepickerTranslations();
  const datepickerValue = toDateValue(state.dateRange);
  const dateRangePlaceholder = t(
    "subIBReport_dateRangePlaceholder",
    "DD MMM YYYY - DD MMM YYYY",
  );

  return (
    /* Canvas `.toolbar { display: flex; align-items: flex-end; gap: 24px }` with
       flat children — search, date, filter, then `.toolbar__actions
       { margin-left: auto }`. Both tabs are one shared `<Toolbar>` in the canvas
       (`SubIBReport.tsx` renders a single instance outside the view switch), so
       this must stay identical to the Shared rebate programs toolbar or the
       controls shift when switching tabs. Was `gap-3` (12px) in a nested
       `justify-between` group. */
    <div className="flex items-end gap-6" data-testid="sub-ib-toolbar">
      {/* Canvas `.search { width: 440px; max-width: 100% }` — a *definite* width,
          not `w-full` with a cap, which resolved against the old nested group
          rather than the toolbar and collapsed the field below 440px. */}
      <div className="relative w-[440px] max-w-full">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <SearchIcon className="h-4 w-4 text-icon-tertiary" />
        </span>
        <input
          type="text"
          value={state.searchQuery}
          onChange={(e) => actions.setSearchQuery(e.target.value)}
          placeholder={t(
            "subIBReport_searchSubsPlaceholder",
            "Search name, email, or comment",
          )}
          data-testid="sub-ib-search"
          className={SEARCH_INPUT_CLASSNAME}
        />
        {state.searchQuery.length > 0 && (
          <button
            type="button"
            onClick={() => actions.setSearchQuery("")}
            aria-label="Clear search"
            className="absolute inset-y-0 right-3 flex items-center text-text-secondary hover:text-text-primary"
          >
            <CrossIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* Design change #4 (PART-3930): the Payout period is the field variant —
          a tonal pill showing the matched shortcut name ("Last 30 days") with a
          calendar icon — not an editable text input. Mirrors the Shared rebate
          programs picker exactly (same FieldTrigger, same configs), so the
          control does not shift when switching tabs. FieldTrigger brings its own
          `h-10 min-w-[260px]`, so this wrapper only positions. */}
      <div className="relative w-[260px]">
        <Datepicker
          value={datepickerValue}
          onChange={(value) => actions.setDateRange(toDateRange(value))}
          useRange
          showShortcuts
          showFooter
          // Read-only: the range is picked from the calendar, never typed.
          readOnly
          // No future dates — reports only ever cover elapsed periods.
          maxDate={new Date()}
          configs={datepickerConfigs}
          displayFormat="DD MMM YYYY"
          separator="-"
          placeholder={dateRangePlaceholder}
          inputId="sub-ib-date-range"
          containerClassName="relative"
          trigger={(triggerProps) => (
            <FieldTrigger
              {...triggerProps}
              allowClear={false}
              placeholder={dateRangePlaceholder}
              // Collapses an exact-match range to its preset name, so the
              // default reads "Last 30 days" rather than two dates.
              shortcutButtonLabel={getShortcutButtonLabel(
                datepickerValue,
                datepickerConfigs,
              )}
            />
          )}
        />
      </div>
      <ButtonV2
        variant="tonal"
        size="md"
        onClick={onFilterOpen}
        data-testid="sub-ib-filter-button"
      >
        <span className="flex items-center gap-2">
          <FilterIcon
            className="h-4 w-4"
            aria-hidden="true"
            focusable="false"
          />
          <Typo element="span" type="labelSM">
            {t("subIBReport_filterButton", "Filter")}
          </Typo>
          {filterCount > 0 && (
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-brand-default px-1.5">
              <Typo
                element="span"
                type="label2XS"
                textColor="text-text-inverse-primary"
              >
                {filterCount}
              </Typo>
            </span>
          )}
        </span>
      </ButtonV2>
      {/* Canvas `.toolbar__actions { margin-left: auto }` — the actions group is a
          flat sibling pushed right, not the far half of a `justify-between`. */}
      <div className="ml-auto flex items-center gap-4">
        <ButtonV2 variant="tonal" size="md" onClick={onExport}>
          Export CSV
        </ButtonV2>
      </div>
    </div>
  );
};
